import { defaultLocale, localeCodes, type Locale } from "./config";

export type PageId = "home" | "dreamjar" | "dreamjarPrivacy" | "dreamjarTerms" | "notFound";

export type RouteMatch = {
  locale: Locale;
  pageId: PageId;
  path: string;
};

const pageSegments: Record<Exclude<PageId, "notFound">, string[]> = {
  home: [],
  dreamjar: ["dreamjar"],
  dreamjarPrivacy: ["dreamjar", "privacy"],
  dreamjarTerms: ["dreamjar", "terms"]
};

export const routedPageIds = Object.keys(pageSegments) as Exclude<PageId, "notFound">[];

export function localizedPath(pageId: Exclude<PageId, "notFound">, locale: Locale): string {
  const prefix = locale === defaultLocale ? "" : `/${locale}`;
  const segments = pageSegments[pageId];
  if (segments.length === 0) {
    return `${prefix || "/"}`;
  }
  return `${prefix}/${segments.join("/")}/`;
}

export function allStaticRoutes(): RouteMatch[] {
  return localeCodes.flatMap((locale) =>
    routedPageIds.map((pageId) => ({
      locale,
      pageId,
      path: localizedPath(pageId, locale)
    }))
  );
}

export function matchRoute(inputPath: string): RouteMatch {
  const normalized = normalizePath(inputPath);
  const segments = normalized.split("/").filter(Boolean);
  const maybeLocale = segments[0] as Locale | undefined;
  const locale = maybeLocale && localeCodes.includes(maybeLocale) ? maybeLocale : defaultLocale;
  const routeSegments = locale === defaultLocale ? segments : segments.slice(1);
  const pageId = pageIdForSegments(routeSegments);

  return {
    locale,
    pageId,
    path: pageId === "notFound" ? normalized : localizedPath(pageId, locale)
  };
}

export function alternatesFor(pageId: Exclude<PageId, "notFound">): Record<Locale | "x-default", string> {
  const alternates = Object.fromEntries(
    localeCodes.map((locale) => [locale, localizedPath(pageId, locale)])
  ) as Record<Locale, string>;
  return { ...alternates, "x-default": localizedPath(pageId, defaultLocale) };
}

function pageIdForSegments(segments: string[]): PageId {
  for (const pageId of routedPageIds) {
    const candidate = pageSegments[pageId];
    if (segments.length === candidate.length && segments.every((segment, index) => segment === candidate[index])) {
      return pageId;
    }
  }
  return "notFound";
}

function normalizePath(inputPath: string): string {
  const withoutQuery = inputPath.split(/[?#]/)[0] || "/";
  const withLeadingSlash = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  if (withLeadingSlash !== "/" && withLeadingSlash.endsWith("/")) {
    return withLeadingSlash;
  }
  return withLeadingSlash;
}
